/**
 * WeaveIn.ai Platform - Hard-Copy Document Tracking API Service
 * Manages offline/physical document lifecycle: generate, print, provide, sign, upload.
 */

import axios, { AxiosInstance } from 'axios';
import { config as productConfig } from '@/src/config';
import type {
  HardCopyTrackingRecord,
  CreateTrackingRequest,
  PrintRecord,
  ProvideRecord,
  SignatureRecord,
  CancelRecord,
  OrgTrackingSettings,
  TrackingHistoryResponse,
} from '@/types/hardCopyTracking';

// ============================================================================
// API CLIENT
// ============================================================================

class HardCopyTrackingAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: { 'Content-Type': 'application/json' },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      if (cboId) config.headers['X-CBO-ID'] = cboId;
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('HardCopyTracking API Error:', {
          status: error.response?.status,
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
          message: error.response?.data?.detail || error.message,
        });
        return Promise.reject(error);
      },
    );
  }

  // ---------- CRUD ----------

  async create(data: CreateTrackingRequest): Promise<HardCopyTrackingRecord> {
    const res = await this.client.post('/api/hard-copy-tracking', data);
    return res.data;
  }

  async list(params?: Record<string, any>): Promise<HardCopyTrackingRecord[]> {
    const res = await this.client.get('/api/hard-copy-tracking', { params });
    return res.data;
  }

  async get(id: number): Promise<HardCopyTrackingRecord> {
    const res = await this.client.get(`/api/hard-copy-tracking/${id}`);
    return res.data;
  }

  // ---------- Lifecycle transitions ----------

  async recordPrint(id: number, data?: PrintRecord): Promise<HardCopyTrackingRecord> {
    const res = await this.client.post(`/api/hard-copy-tracking/${id}/print`, data || {});
    return res.data;
  }

  async recordProvide(id: number, data?: ProvideRecord): Promise<HardCopyTrackingRecord> {
    const res = await this.client.post(`/api/hard-copy-tracking/${id}/provide`, data || {});
    return res.data;
  }

  async recordSignature(id: number, data: SignatureRecord): Promise<HardCopyTrackingRecord> {
    const res = await this.client.post(`/api/hard-copy-tracking/${id}/record-signature`, data);
    return res.data;
  }

  async uploadSignedCopy(id: number, file: File, changeNotes?: string): Promise<HardCopyTrackingRecord> {
    const formData = new FormData();
    formData.append('file', file);
    if (changeNotes) formData.append('change_notes', changeNotes);

    const res = await this.client.post(
      `/api/hard-copy-tracking/${id}/upload-signed-copy`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
  }

  async cancel(id: number, data: CancelRecord): Promise<HardCopyTrackingRecord> {
    const res = await this.client.post(`/api/hard-copy-tracking/${id}/cancel`, data);
    return res.data;
  }

  // ---------- Audit trail ----------

  async getHistory(id: number): Promise<TrackingHistoryResponse> {
    const res = await this.client.get(`/api/hard-copy-tracking/${id}/history`);
    return res.data;
  }

  // ---------- Org settings ----------

  async getOrgSettings(): Promise<OrgTrackingSettings> {
    const res = await this.client.get('/api/hard-copy-tracking/org-settings');
    return res.data;
  }

  async updateOrgSettings(prefix: string): Promise<OrgTrackingSettings> {
    const res = await this.client.put('/api/hard-copy-tracking/org-settings', { prefix });
    return res.data;
  }
}

export const hardCopyTrackingAPI = new HardCopyTrackingAPI();
export default hardCopyTrackingAPI;
